import Vue from 'vue';
import Component from 'vue-class-component';
import {mapGetters} from 'vuex';
import {Prop, Watch} from 'vue-property-decorator';
import {CustomerDataDTO, SessionDTO} from '../../../DTOs';
import CustomerForm from '../../Checkout/CustomerForm/CustomerForm.vue';
import {AuthService, OrderService, RestClient, VisitorSegmentService} from '../../../Services';
import {
  CLEAR_BUYER_DATA,
  CLEAR_IS_GUEST_ORDER, CLEAR_LOGIN_RETURN_URL,
  CLEAR_SESSION, CLEAR_USER,
  SET_BUYER_DATA,
  SET_IS_GUEST_ORDER,
  SET_LOGIN_RETURN_URL, SET_USER,
} from '../../../store/commits';
import {CartMixin} from '../../../Mixins';
import PersonalUseModal from '../../Components/PersonalUseModal/PersonalUseModal.vue';
import Login from '../../Icons/Login.vue';
import LoginGuest from '../../Icons/LoginGuest.vue';
import LoginRegister from '../../Icons/LoginRegister.vue';
import {Country, OrderItem, State, User, VisitorSegment} from '../../../Models';
import {AddressTypeEnum, VisitorSegmentEnum} from '../../../Enums';
import Modal from '../../Modal/Modal.vue';

const LOGIN_OPTIONS = {
  LOGIN: 'LOGIN',
  REGISTRATION: 'REGISTRATION',
  GUEST: 'GUEST',
};

@Component({
  components: {
    CustomerForm,
    PersonalUseModal,
    Login,
    LoginGuest,
    LoginRegister,
    Modal,
  },
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      currentBuyerData: 'getBuyerData',
      sessionDTO: 'getSession',
      isGuestOrder: 'getIsGuestOrder',
      countries: 'getCountries',
      statesMapping: 'getStates',
      loginReturnUrl: 'getLoginReturnUrl',
      registrationCodes: 'getCurrentRegistrationCodes',
    }),
  },
})
export default class LoginView extends CartMixin {
  @Prop({type: String, required: true}) loginUrl: string | undefined;
  @Prop({type: String, required: true}) checkoutUrl: string | undefined;
  @Prop({type: String, required: true}) ticketShopUrl: string | undefined;
  @Prop({type: String, required: true}) loginBlanko: string | undefined;

  isCreatingGuest: boolean = false;
  currentBuyerData: CustomerDataDTO | undefined;
  sessionDTO: SessionDTO | undefined;
  loginReturnUrl: string | undefined;
  guestFormIsValid: boolean = false;
  isZipValid: boolean = true;
  showErrors: boolean = false;

  guestDTO: CustomerDataDTO = new CustomerDataDTO();
  selectedOption: string = LOGIN_OPTIONS.LOGIN;
  activeOption: number = 1;

  authService!: AuthService;
  visitorSegmentService!: VisitorSegmentService;
  restClient: RestClient = new RestClient();

  private countries!: Array<Country>;
  private statesMapping!: { [key: string]: Array<State> };
  private _loginReturnUrl: string | null = null;
  private visitorSegments: Array<VisitorSegment> = [];
  private registrationCodes!: Array<string> | null;
  private visitorSegment: VisitorSegment | undefined;

  created() {
    this.authService = new AuthService(this.loginUrl);
    this.orderService = new OrderService(this.inventoryUrl);
    this.visitorSegmentService = new VisitorSegmentService(this.inventoryUrl);

    this.storeReturnUrl();
  }

  async mounted() {
    const hasSentDataLayerLogin = sessionStorage.getItem('sentDataLayerLogin') === '1';
    if (!hasSentDataLayerLogin) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'checkout_login',
      });
      sessionStorage.setItem('sentDataLayerLogin', '1');
    }

    this.isRequesting = true;
    await this.$store.dispatch('fetchMiscData', this.inventoryUrl);
    // when getting redirected from login page
    if (this.sessionDTO && this.sessionDTO.session) {
      await this.setBuyerData();
    } else if (this.currentBuyerData && !this.isGuestOrder) {
      this.cleanupUserData();
    }
    // We only need to fetch the visitor-segments, if registering a ticket
    // this is to check if the user is allowed to use an email as guest, even if it exists as a registered user already
    if (this.isRegisteringATicket) {
      await this.setVisitorSegmentIfRegisteringTicket();
    }

    this.isRequesting = false;

    // important to be after this
    if (this.mustOrderAsGuest && !this.mustOrderWithLogin) {
      this.selectGuest();
    }
  }

  async redirectToRegistration() {
    // go to the registration page when there is no customer data available
    let regUrl = `${this.loginUrl}/${this.userLanguage}/registration`;
    const queryParams = new URLSearchParams();
    queryParams.set('returnUrl', this.currentUrl);

    if (this.currentBuyerData) {
      // attempt creating a guest registration and let the user complete the registration process
      const token = await this.authService.createTemporaryGuestProfile(this.currentBuyerData);
      if (token) {
        regUrl = `${regUrl}/confirm/${token}`;
      }
    }
    regUrl += `?${queryParams.toString()}`;
    window.location.href = regUrl;
  }

  async submitGuestForm() {
    this.showErrors = true;
    if (!this.guestFormIsValid || !this.isZipValid) {
      return;
    }
    this.isCreatingGuest = true;
    try {
      if (await this.createGuestProfile()) {
        this.$store.commit(SET_BUYER_DATA, this.guestDTO);
        this.$store.commit(SET_IS_GUEST_ORDER, true);
        this.isCreatingGuest = false;
      }
    } catch (e) {
      console.error(e);
      this.isCreatingGuest = false;
      this.showToast(<string>this.$t('toasts.knownMail'), 10000);
      return null;
    }
  }

  async createGuestProfile(): Promise<User | null | boolean> {
    if (this.allowSameMailAsGuest) {
      return true;
    }
    if (!this.mustOrderWithLogin) {
      return this.authService.createGuestProfile(this.guestDTO);
    }
    return null;
  }

  async setBuyerData() {
    if (this.sessionDTO && this.sessionDTO.session) {
      this.cleanupUserData();
      const user = await this.authService.fetchUserData(this.sessionDTO.session);
      this.$store.commit(SET_USER, user);
      const buyerData = (new CustomerDataDTO()).fromData(user);
      this.$store.commit(SET_BUYER_DATA, buyerData);
    }
  }

  async setVisitorSegmentIfRegisteringTicket() {
    this.visitorSegments = await this.visitorSegmentService.list();
    this.visitorSegment = this.visitorSegments.find(
      (vs: VisitorSegment) => vs.id === this.products[0].visitorSegmentId);
  }

  changeBuyerData() {
    this.logout(this.isGuestOrder);
    this.closeVerificationModal();
  }

  selectLogin() {
    this.selectedOption = LOGIN_OPTIONS.LOGIN;
    this.activeOption = 1;
  }

  selectRegistration() {
    this.selectedOption = LOGIN_OPTIONS.REGISTRATION;
    this.activeOption = 2;
  }

  selectGuest() {
    this.selectedOption = LOGIN_OPTIONS.GUEST;
    this.activeOption = 3;
  }

  updateGuestDTO(updateData: { dto: CustomerDataDTO, missingRequiredFields: Array<string> }) {
    Object.keys(updateData.dto).forEach((k) => {
      Vue.set(this.guestDTO, k, updateData.dto[k]);
    });
    const isMailMatching = updateData.dto.email.toLowerCase() === updateData.dto.emailConfirm.toLowerCase();
    this.guestFormIsValid = updateData.missingRequiredFields.length === 0 && isMailMatching;
  }

  async logout(guest = false) {
    let success = true;
    if (this.sessionDTO && this.sessionDTO.session) {
      success = await this.authService.logout(this.sessionDTO.session);
    }

    if (success) {
      this.cleanupUserData();
      this.$store.commit(CLEAR_SESSION);
      this.updateWindowUrl();
      if (!this.mustOrderAsGuest) {
        this.selectLogin();
      } else {
        this.selectGuest();
      }
      if (!guest) {
        this.showToast(<string>this.$t('toasts.logOutSuccess'));
      } else {
        this.showToast(<string>this.$t('toasts.guestDataRemoved'));
      }
    } else {
      this.showToast(<string>this.$t('toasts.logOutError'));
    }
  }

  storeReturnUrl() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('returnUrl') && (searchParams.get('returnUrl') || '').trim().length > 0) {
      try {
        const returnUrl = new URL(searchParams.get('returnUrl') || '');
        if (returnUrl.host === window.location.host) {
          this.$store.commit(SET_LOGIN_RETURN_URL, returnUrl.toString());
          this._loginReturnUrl = returnUrl.toString();
        }
      } catch (e) {
        console.error(e);
      }
    }
    this._loginReturnUrl = `${this.loginReturnUrl}`;
  }

  clearReturnUrl() {
    this._loginReturnUrl = `${this.loginReturnUrl}`;
    this.$store.commit(CLEAR_LOGIN_RETURN_URL);
  }

  get continuationUrl(): string {
    if (this._loginReturnUrl && this.currentBuyerData) {
      try {
        const url = new URL(this._loginReturnUrl);
        // only allow same page returns
        if (url.host === window.location.host) {
          return url.toString();
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (this.cart && ((!this.isGuestOrder && (!this.mustOrderAsGuest || this.mustOrderWithLogin)) || (this.isGuestOrder && !this.mustOrderWithLogin))) {
      return this.checkoutUrl!;
    }

    return this.ticketShopUrl!;
  }

  get needsVerificationModal() {
    // Only show modal if at least one orderItem has RegistrationRequired set to "Ja"
    // @ts-ignore
    const orderItemWithRegistrationReqYes =
      this.cart?.order.orderItems.find(orderItem => orderItem.registrationRequired === 'Ja');
    if (!orderItemWithRegistrationReqYes) {
      return false;
    }
    return (this.loginReturnUrl && this.currentBuyerData)
      || (this.cart && ((!this.isGuestOrder && (!this.mustOrderAsGuest || this.mustOrderWithLogin)) || (this.isGuestOrder && !this.mustOrderWithLogin)));
  }

  openVerificationModal() {
    if (this.$refs.addressVerificationModal) {
      // @ts-ignore
      this.$refs.addressVerificationModal.showModal();
    }
  }

  closeVerificationModal() {
    if (this.$refs.addressVerificationModal) {
      // @ts-ignore
      this.$refs.addressVerificationModal.closeModal(true);
    }
  }

  updateWindowUrl() {
    history.replaceState(null, document.title, window.location.pathname);
  }

  toTicketShop() {
    if (this.ticketShopUrl) {
      window.location.href = this.ticketShopUrl;
    }
  }

  toLogin() {
    window.location.href = this.authService.getLoginUrl(this.currentUrl, this.userLanguage || 'de');
  }

  stateTitle(stateId: number) {
    if (stateId) {
      const state = this.stateChoices.find(stateOption => stateOption.id === stateId);
      if (state) {
        if (this.userLanguage !== 'en' && state.translations && state.translations[this.userLanguage]) {
          return state.translations[this.userLanguage].title;
        }
        return state.title;
      }
      return stateId;
    }
    return '-';
  }

  countryTitle(countryId: number) {
    if (countryId) {
      const country = this.countries.find(countryOption => countryOption.id === countryId);
      if (country) {
        if (this.userLanguage !== 'en' && country.translations && country.translations[this.userLanguage]) {
          return country.translations[this.userLanguage].title;
        }
        return country.title;
      }
      return countryId;
    }
    return '';
  }

  cleanupUserData() {
    this.$store.commit(CLEAR_IS_GUEST_ORDER);
    this.$store.commit(CLEAR_BUYER_DATA);
    this.$store.commit(CLEAR_USER);
  }

  setIsZipValid(value: boolean) {
    this.isZipValid = value;
  }

  @Watch('currentOrder.orderItems')
  async onOrderItemsChange() {
    // re-load available data in case the user removes a positions from his order. In that case, events might change and
    // guest order might be enabled again
    await this.$store.dispatch('fetchInitialData', {inventoryUrl: this.inventoryUrl, cart: this.cart});
  }

  isDisabledByQuery(option) {
    return !this.activatedOptionsFromQuery.includes(option);
  }

  showCompany(addressType: number) {
    // only required if address-type is non-private
    return addressType === AddressTypeEnum.COMMERCIAL_BUSINESS.value;
  }

  /**
   * It is possible (depending on the use case) to disable various login options like guest login via get param
   * Allowed values: guest, registration, login
   */
  get activatedOptionsFromQuery() {
    const search = window.location.search;
    const q = new URLSearchParams(search);
    const allowed = ['guest', 'registration', 'login'];
    if (q.getAll('options[]') && q.getAll('options[]').length > 0) {
      let options = q.getAll('options[]');
      return options.filter(o => allowed.includes(o));
    }
    return allowed;
  }

  get currentUrl(): string {
    return window.location.href.split('?')[0];
  }

  get loggedIn(): boolean {
    return Boolean(this.currentBuyerData);
  }

  get isLoginSelected(): boolean {
    return this.selectedOption === LOGIN_OPTIONS.LOGIN;
  }

  get isRegistrationSelected(): boolean {
    return this.selectedOption === LOGIN_OPTIONS.REGISTRATION;
  }

  get isGuestSelected(): boolean {
    return this.selectedOption === LOGIN_OPTIONS.GUEST;
  }

  get stateChoices() {
    if (this.statesMapping && this.currentBuyerData && this.currentBuyerData.country && this.statesMapping[this.currentBuyerData.country]) {
      return this.statesMapping[this.currentBuyerData.country];
    }
    return [];
  }

  get blankoEnabled() {
    return parseInt(this.loginBlanko!, 10) === 1;
  }

  get isRegisteringATicket(): boolean {
    return !!this.products[0]?.visitorSegmentId && !!this.registrationCodes && !!this.registrationCodes[0];
  }

  get isAbbau(): boolean | undefined {
    return this.visitorSegment?.title === VisitorSegmentEnum.ABBAU;
  }

  get isAufbau(): boolean | undefined {
    return this.visitorSegment?.title === VisitorSegmentEnum.AUFBAU;
  }

  get isAussteller(): boolean | undefined {
    return this.visitorSegment?.title === VisitorSegmentEnum.AUSSTELLER;
  }

  get allowSameMailAsGuest(): boolean | undefined {
    return (this.isAbbau || this.isAufbau || this.isAussteller) && this.isRegisteringATicket;
  }
}
